// app/features/industry-opportunities/components/OpportunityCardComponent.tsx
// Updated: Card styling to match homepage cards - bigger, more substantial
'use client';

import { BarChart3, Database, FileText,Settings, Sparkles, Users, Zap } from 'lucide-react';
import Link from 'next/link';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { IndustryOpportunity } from '../industry-opportunity.interface';
import { CATEGORY_CONFIG as CATEGORY_CONFIG_BASE, getCategoryKey } from '../industry-opportunity.utils';

interface OpportunityCardComponentProps {
  opportunity: IndustryOpportunity;
  industrySlug: string;
  className?: string;
}

// Icon mapping for client-side rendering
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  'Users': Users,
  'Zap': Zap,
  'BarChart3': BarChart3,
  'Settings': Settings,
  'Database': Database,
  'FileText': FileText,
  'Sparkles': Sparkles,
};

// Extended config with actual icon components
const CATEGORY_CONFIG = Object.fromEntries(
  Object.entries(CATEGORY_CONFIG_BASE).map(([key, config]) => [
    key,
    { ...config, icon: CATEGORY_ICONS[config.iconName] || Sparkles }
  ])
) as Record<string, typeof CATEGORY_CONFIG_BASE[string] & { icon: React.ElementType }>;

// Get the card description from metadata or solution headline
function getCardDescription(opportunity: IndustryOpportunity): string {
  // Check for override in metadata first
  if (opportunity.metadata_json && typeof opportunity.metadata_json === 'object' && 'card_description' in opportunity.metadata_json) {
    const cardDesc = opportunity.metadata_json.card_description;
    if (typeof cardDesc === 'string' && cardDesc.length > 0) {
      return cardDesc;
    }
  }
  return '';
}

// Get a shorter solution name for the card
function getSolutionName(opportunity: IndustryOpportunity): string {
  // Check for solution name in metadata
  if (opportunity.metadata_json && typeof opportunity.metadata_json === 'object' && 'solution_name' in opportunity.metadata_json) {
    const name = opportunity.metadata_json.solution_name;
    if (typeof name === 'string' && name.length > 0) {
      return name;
    }
  }
  
  // Fall back to solution headline, truncated if needed
  if (opportunity.solution_headline) {
    if (opportunity.solution_headline.length < 40) {
      return opportunity.solution_headline;
    }
    const headline = opportunity.solution_headline;
    const breakPoints = [' in ', ' - ', ': ', ', ', ' for '];
    for (const bp of breakPoints) {
      const idx = headline.indexOf(bp);
      if (idx > 15 && idx < 45) {
        return headline.substring(0, idx);
      }
    }
    return headline.substring(0, 35) + '...';
  }
  return 'AI-Powered Solution';
}

export function OpportunityCardComponent({ 
  opportunity, 
  industrySlug,
  className 
}: OpportunityCardComponentProps) {
  const categoryKey = getCategoryKey(opportunity);
  const categoryConfig = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG['custom'];
  const solutionName = getSolutionName(opportunity);
  const cardDescription = getCardDescription(opportunity);
  const hasGenerated = !!opportunity.solution_headline;

  // Special case: AI Account Manager should link to its dedicated page
  const isAccountManager = opportunity.solution_headline?.toLowerCase().includes('account manager');
  const href = isAccountManager 
    ? '/solutions/ai-account-manager'
    : `/solutions/industries/${industrySlug}/${opportunity.slug}`;

  return (
    <Link 
      href={href}
      className={cn("block group", className)}
    >
      <div className="bg-beige-light rounded-2xl shadow-sm overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
        {/* Top accent bar - category colored (thicker for presence) */}
        <div className={cn("h-2", categoryConfig.accentColor)} />

        <div className="p-8 flex-grow flex flex-col">
          {/* Solution Name badge - solid background with white text */}
          <div className="flex items-center justify-between mb-5">
            <Badge className={cn(
              "text-sm text-white font-semibold px-4 py-1.5 rounded-lg",
              categoryConfig.accentColor
            )}>
              {solutionName}
            </Badge>
            
            {opportunity.is_featured && (
              <Badge className="bg-orange-500 text-white text-xs py-1 px-3">
                <Sparkles size={12} className="mr-1" />
                Featured
              </Badge>
            )}
          </div>

          {/* Problem Title - the pain point */}
          <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2 group-hover:text-gray-700 transition-colors leading-tight">
            {opportunity.problem_title}
          </h3>

          {/* Card Description - the benefit/outcome */}
          <p className={cn(
            "text-lg leading-relaxed flex-grow",
            hasGenerated && cardDescription ? "text-gray-700" : "text-gray-400 italic"
          )}>
            {hasGenerated && cardDescription 
              ? cardDescription 
              : hasGenerated 
                ? 'Custom AI solution available' 
                : 'Solution details coming soon'
            }
          </p>

          {/* Subtle CTA hint - hover uses card accent color */}
          <div className="mt-6 pt-4 border-t border-beige-dark">
            <span className={cn(
              "text-sm font-medium text-gray-500 transition-colors",
              categoryConfig.hoverTextColor
            )}>
              Learn more →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Compact variant for sidebars or smaller displays
export function OpportunityCardCompactComponent({ 
  opportunity, 
  industrySlug,
  className 
}: OpportunityCardComponentProps) {
  const categoryKey = getCategoryKey(opportunity);
  const categoryConfig = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG['custom'];
  
  return (
    <Link 
      href={`/solutions/industries/${industrySlug}/${opportunity.slug}`}
      className={cn("block group", className)}
    >
      <div className="bg-beige-light rounded-xl overflow-hidden transition-all duration-200 hover:shadow-md">
        <div className={cn("h-1.5", categoryConfig.accentColor)} />
        <div className="p-4">
          <h4 className="font-semibold text-base text-gray-800 group-hover:text-gray-600 transition-colors line-clamp-2">
            {opportunity.problem_title}
          </h4>
        </div>
      </div>
    </Link>
  );
}

